import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, Card, Notice, useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';
import { useMemberships } from '@/lib/authStore';

/**
 * PR 7 — student's Memberships page. `/profile/memberships`. Lists every Membership row
 * (verified / pending / rejected / ended) so the student sees their full lifecycle history.
 * Actions: Set as primary (verified), Retry claim (rejected), View placement history (ended).
 */

interface MembershipDetail {
  id: string;
  tenantId: string | null;
  tenantName: string;
  tenantSlug: string;
  tenantKind: string;
  relationship: string;
  status: 'pending' | 'verified' | 'rejected' | 'ended';
  verificationMethod?: string;
  primary: boolean;
  verifiedAt?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  endedAt?: string | null;
  endedReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const RELATIONSHIP_LABEL: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  alumni: 'Alumni',
  public: 'Public catalog',
  dual_degree: 'Dual degree',
  intern: 'Intern',
  staff: 'Staff',
  other: 'Other',
};

const STATUS_CHIP_CLASS: Record<string, string> = {
  verified: 'chip chip-success',
  pending: 'chip chip-warning',
  rejected: 'chip chip-danger',
  ended: 'chip',
  imported: 'chip chip-info',
};

function shortDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

export function StudentMembershipsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const primaryMembership = useMemberships().find((m) => m.primary);

  const memberships = useQuery({
    queryKey: ['student', 'me', 'memberships'],
    queryFn: () => apiGet<{ memberships: MembershipDetail[] }>('/students/me/memberships'),
  });

  const setPrimary = useMutation({
    mutationFn: (membershipId: string) =>
      apiPost<{ membershipId: string; primary: boolean }>(
        `/students/me/memberships/${membershipId}/set-primary`,
      ),
    onSuccess: () => {
      // Deep-audit fix (Page 23/N): old toast said 'Reload to see the
      // switcher reflect your new primary workspace' — but we already
      // invalidate auth/me right below, so TanStack Query refetches and
      // the switcher updates without a reload. The 'reload' instruction
      // was misleading — asking the user to do something we did for
      // them. Now says what actually happened.
      toast.show({
        title: 'Primary membership updated',
        message: 'Your workspace switcher now points here.',
      });
      void qc.invalidateQueries({ queryKey: ['student', 'me', 'memberships'] });
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err) => {
      toast.show({
        title: "Couldn't set primary",
        message: err instanceof Error ? err.message : 'Please try again.',
        tone: 'danger',
      });
    },
  });

  const rows: MembershipDetail[] = useMemo(
    () => memberships.data?.memberships ?? [],
    [memberships.data],
  );

  return (
    <div className="grid gap-4">
      <header className="grid gap-1">
        <h1 className="h-page">My memberships</h1>
        <p className="text-sm" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          Every college or tenant you belong to on Kollez. Kollez Public is always here — it
          gives you the public catalog, career advisor, and prep tools regardless of your
          college status.
        </p>
      </header>

      {primaryMembership ? (
        <Notice tone="info">
          Your primary workspace right now is <b>{primaryMembership.tenantName}</b>. That's where
          your placement outcomes are reported.
        </Notice>
      ) : null}

      <AsyncBoundary
        kind="cards"
        loading={memberships.isLoading}
        error={memberships.isError ? memberships.error : undefined}
        empty={!memberships.isLoading && rows.length === 0}
      >
        <div className="grid gap-3">
          {rows.map((m) => (
            <Card key={m.id}>
              <div className="grid gap-3">
                <div className="flex justify-between items-start" style={{ gap: 12 }}>
                  <div className="grid" style={{ gap: 4 }}>
                    <div className="flex items-center" style={{ gap: 8 }}>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{m.tenantName}</h3>
                      {m.primary ? <span className="chip chip-success">Primary</span> : null}
                    </div>
                    <div className="flex items-center" style={{ gap: 8 }}>
                      <span className={STATUS_CHIP_CLASS[m.status] ?? 'chip'}>
                        {m.status}
                      </span>
                      <span className="text-sm" style={{ color: 'rgb(var(--ink-tertiary))' }}>
                        {RELATIONSHIP_LABEL[m.relationship] ?? m.relationship}
                      </span>
                    </div>
                  </div>
                  <div className="grid gap-2" style={{ justifyItems: 'end' }}>
                    {m.status === 'verified' && !m.primary ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setPrimary.mutate(m.id)}
                        disabled={setPrimary.isPending}
                      >
                        Set as primary
                      </button>
                    ) : null}
                    {m.status === 'ended' ? (
                      <Link className="btn btn-ghost btn-sm" to="/applications">
                        View placement history
                      </Link>
                    ) : null}
                    {m.status === 'rejected' && m.tenantId ? (
                      <Link
                        className="btn btn-ghost btn-sm"
                        to={`/register/student?add_membership=1&tenant=${encodeURIComponent(m.tenantId)}`}
                      >
                        Retry claim
                      </Link>
                    ) : null}
                  </div>
                </div>

                <dl
                  className="grid"
                  style={{
                    gridTemplateColumns: 'auto 1fr',
                    gap: '4px 12px',
                    margin: 0,
                    fontSize: 12,
                    color: 'rgb(var(--ink-secondary))',
                  }}
                >
                  <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Verified via</dt>
                  <dd style={{ margin: 0 }}>{m.verificationMethod ?? '—'}</dd>
                  {m.verifiedAt ? (
                    <>
                      <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Verified on</dt>
                      <dd style={{ margin: 0 }}>{shortDate(m.verifiedAt)}</dd>
                    </>
                  ) : null}
                  {m.status === 'rejected' && m.rejectionReason ? (
                    <>
                      <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Rejection reason</dt>
                      <dd style={{ margin: 0 }}>{m.rejectionReason}</dd>
                    </>
                  ) : null}
                  {m.status === 'ended' ? (
                    <>
                      <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Ended on</dt>
                      <dd style={{ margin: 0 }}>{shortDate(m.endedAt)}</dd>
                      {m.endedReason ? (
                        <>
                          <dt style={{ color: 'rgb(var(--ink-tertiary))' }}>Reason</dt>
                          <dd style={{ margin: 0 }}>{m.endedReason}</dd>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </dl>
              </div>
            </Card>
          ))}
        </div>
      </AsyncBoundary>

      <div style={{ marginTop: 12 }}>
        <Link className="btn btn-primary" to="/register/student?add_membership=1">
          + Add another college
        </Link>
      </div>
    </div>
  );
}
